/**
 * WHAT: Settings and security rules management for the Custom SQL Executor module.
 * WHY: Administrators need to configure execution limits, security policies, and
 *      SQL validation rules to prevent destructive operations while allowing
 *      legitimate UAR data extraction queries.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api.config';

/**
 * WHAT: Settings component with configuration tabs for execution, security,
 *       retention, and notification settings, plus SQL security rules management.
 * WHY: Provides administrative control over the Custom SQL Executor to enforce
 *      organizational security policies and prevent unauthorized data modifications.
 */
const CustomSettings = () => {
  const [settings, setSettings] = useState([]);
  const [securityRules, setSecurityRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [activeTab, setActiveTab] = useState('settings');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [settingsRes, rulesRes] = await Promise.all([
        axios.get(`${API_CONFIG.BASE_URL}/api/custom/settings/`),
        axios.get(`${API_CONFIG.BASE_URL}/api/custom/security-rules/`),
      ]);
      setSettings(settingsRes.data.settings || []);
      setSecurityRules(rulesRes.data.rules || []);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSetting = async (settingKey, newValue) => {
    setSaving(settingKey);
    try {
      await axios.put(`${API_CONFIG.BASE_URL}/api/custom/settings/${settingKey}/`, {
        value: newValue,
      });
      // Update local state
      setSettings(settings.map(s =>
        s.setting_key === settingKey ? { ...s, setting_value: newValue } : s
      ));
    } catch (err) {
      console.error('Error updating setting:', err);
      alert('Failed to update setting');
    } finally {
      setSaving(null);
    }
  };

  const groupedSettings = settings.reduce((acc, setting) => {
    const category = setting.category || 'GENERAL';
    if (!acc[category]) acc[category] = [];
    acc[category].push(setting);
    return acc;
  }, {});

  const categoryIcons = {
    EXECUTION: 'fa-play-circle',
    SECURITY: 'fa-shield-alt',
    RETENTION: 'fa-clock',
    NOTIFICATION: 'fa-bell',
    GENERAL: 'fa-cog',
  };

  const categoryColors = {
    EXECUTION: 'primary',
    SECURITY: 'danger',
    RETENTION: 'info',
    NOTIFICATION: 'warning',
    GENERAL: 'secondary',
  };

  const getSeverityBadge = (severity) => {
    const badges = {
      CRITICAL: 'bg-danger',
      HIGH: 'bg-warning text-dark',
      MEDIUM: 'bg-info',
      LOW: 'bg-secondary',
    };
    return badges[severity] || 'bg-secondary';
  };

  const renderSettingInput = (setting) => {
    const isDisabled = !setting.is_editable || saving === setting.setting_key;

    switch (setting.setting_type) {
      case 'BOOLEAN':
        return (
          <div className="form-check form-switch">
            <input
              type="checkbox"
              className="form-check-input"
              checked={setting.setting_value === 'true'}
              onChange={(e) => handleUpdateSetting(setting.setting_key, e.target.checked ? 'true' : 'false')}
              disabled={isDisabled}
            />
          </div>
        );
      case 'INTEGER':
        return (
          <input
            type="number"
            className="form-control form-control-sm"
            value={setting.setting_value || ''}
            onChange={(e) => handleUpdateSetting(setting.setting_key, e.target.value)}
            disabled={isDisabled}
            style={{ maxWidth: '150px' }}
          />
        );
      case 'JSON':
        return (
          <textarea
            className="form-control form-control-sm font-monospace"
            rows="2"
            value={setting.setting_value || '[]'}
            onChange={(e) => handleUpdateSetting(setting.setting_key, e.target.value)}
            disabled={isDisabled}
            style={{ fontSize: '11px' }}
          />
        );
      default:
        return (
          <input
            type="text"
            className="form-control form-control-sm"
            value={setting.setting_value || ''}
            onChange={(e) => handleUpdateSetting(setting.setting_key, e.target.value)}
            disabled={isDisabled}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading settings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
        <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchData}>
          <i className="fas fa-sync me-1"></i> Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Tabs */}
      <ul className="nav nav-pills mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <i className="fas fa-sliders-h me-2"></i>
            Configuration
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <i className="fas fa-shield-alt me-2"></i>
            Security Rules
          </button>
        </li>
      </ul>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="row">
          {Object.entries(groupedSettings).map(([category, categorySettings]) => (
            <div className="col-md-6 mb-4" key={category}>
              <div className="card h-100">
                <div className={`card-header bg-${categoryColors[category] || 'secondary'} text-white`}>
                  <h6 className="mb-0">
                    <i className={`fas ${categoryIcons[category] || 'fa-cog'} me-2`}></i>
                    {category} Settings
                  </h6>
                </div>
                <div className="card-body">
                  <table className="table table-sm mb-0">
                    <tbody>
                      {categorySettings.map((setting) => (
                        <tr key={setting.setting_key}>
                          <td style={{ width: '50%' }}>
                            <strong>{setting.setting_key.replace(/_/g, ' ').toLowerCase()}</strong>
                            {setting.description && (
                              <div>
                                <small className="text-muted">{setting.description}</small>
                              </div>
                            )}
                          </td>
                          <td>
                            {renderSettingInput(setting)}
                            {saving === setting.setting_key && (
                              <small className="text-muted">
                                <i className="fas fa-spinner fa-spin ms-2"></i>
                              </small>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Security Rules Tab */}
      {activeTab === 'security' && (
        <div className="card">
          <div className="card-header bg-danger text-white">
            <h5 className="mb-0">
              <i className="fas fa-shield-alt me-2"></i>
              SQL Security Rules ({securityRules.length})
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="alert alert-info m-3 mb-0">
              <i className="fas fa-info-circle me-2"></i>
              These rules are automatically applied to validate SQL templates. Keywords and patterns matching these rules will be blocked.
            </div>
            <table className="table table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Rule Name</th>
                  <th>Type</th>
                  <th>Pattern</th>
                  <th className="text-center">Severity</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {securityRules.map((rule) => (
                  <tr key={rule.rule_id} className={!rule.is_active ? 'table-secondary' : ''}>
                    <td>
                      <strong>{rule.rule_name}</strong>
                      {rule.description && (
                        <div>
                          <small className="text-muted">{rule.description}</small>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge bg-secondary">{rule.rule_type}</span>
                    </td>
                    <td>
                      <code className="text-danger">{rule.pattern}</code>
                    </td>
                    <td className="text-center">
                      <span className={`badge ${getSeverityBadge(rule.severity)}`}>
                        {rule.severity}
                      </span>
                    </td>
                    <td className="text-center">
                      {rule.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="row mt-4">
        <div className="col-md-4">
          <div className="card border-primary">
            <div className="card-body">
              <h6 className="card-title text-primary">
                <i className="fas fa-info-circle me-2"></i>
                About Custom SQL Executor
              </h6>
              <p className="card-text small text-muted">
                This module allows organizations to use their existing UAR scripts with DAMA's automation.
                SQL templates are validated for security before execution.
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-warning">
            <div className="card-body">
              <h6 className="card-title text-warning">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Security Notice
              </h6>
              <p className="card-text small text-muted">
                All SQL templates are validated against security rules. DDL/DML operations (INSERT, UPDATE, DELETE, DROP)
                are blocked by default.
              </p>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-info">
            <div className="card-body">
              <h6 className="card-title text-info">
                <i className="fas fa-database me-2"></i>
                Supported Databases
              </h6>
              <p className="card-text small text-muted">
                Oracle, Microsoft SQL Server, PostgreSQL, MySQL, and Apache Cassandra are supported
                for custom SQL execution.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomSettings;
